/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  Accordion,
  AccordionHeader,
  AccordionPanel,
  Button,
  Divider,
  Grid,
  Typography,
  Typography2,
  useTheme,
} from '@coursera/cds-core';

import type { TabOption } from 'bundles/enterprise-admin-drawers/components/components/TabbedFilterOptionList';
import type {
  UserFilterBuilderError,
  UserFilterBuilderErrors,
} from 'bundles/enterprise-admin-user-filters/components/ErrorsNotification';
import FilterPills from 'bundles/enterprise-admin-user-filters/components/FilterPills';
import ProgramJoinDate from 'bundles/enterprise-admin-user-filters/components/ProgramJoinDate';
import ProgramSelectionDrawer from 'bundles/enterprise-admin-user-filters/components/ProgramSelectionDrawer';
import { Section, SubSection } from 'bundles/enterprise-admin-user-filters/components/ui';
import { getPrimaryFilterLabel } from 'bundles/enterprise-admin-user-filters/constants';
import type { EnrichedData, PrimaryFilter } from 'bundles/enterprise-admin-user-filters/types';
import {
  convertEnrichedProgramsToFilterOptions,
  convertFilterOptionsToEnrichedPrograms,
  convertFilterOptionsToIds,
  normalizeProgramId,
} from 'bundles/enterprise-admin-user-filters/utils';
import { Spacer } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-user-filters';

export type Props = {
  errors: UserFilterBuilderErrors;
  thirdPartyOrganization: { id: string; slug: string };
  programName?: string;
  primaryFilter: PrimaryFilter;
  enrichedData: EnrichedData;
  onChangePrimaryFilter: () => void;
  onPrimaryFilterChange: (change: Partial<PrimaryFilter>) => void;
  onEnrichedProgramsChange: (enrichedPrograms: { programId: string; programName: string }[]) => void;
  onClearError: (error: UserFilterBuilderError) => void;
};

const PrimaryFilterSection: React.VFC<Props> = ({
  errors,
  thirdPartyOrganization,
  programName,
  primaryFilter,
  enrichedData,
  onChangePrimaryFilter,
  onPrimaryFilterChange,
  onEnrichedProgramsChange,
  onClearError,
}) => {
  const theme = useTheme();
  // Pending Heenesh final decision
  const MAX_PROGRAMS_DISPLAYED = 7;
  const selectedProgramIds = primaryFilter.programIdSelection?.values ?? [];

  const [isProgramSelectionDrawerOpen, setIsProgramSelectionDrawerOpen] = React.useState<boolean>(false);
  const [programSelectionDrawerDefaultTab, setProgramSelectionDrawerDefaultTab] = React.useState<
    TabOption | undefined
  >();
  const openProgramSelectionDrawer = (tab: TabOption) => () => {
    setIsProgramSelectionDrawerOpen(true);
    setProgramSelectionDrawerDefaultTab(tab);
  };

  const handleDismissProgram = (id: string) => {
    onPrimaryFilterChange({ programIdSelection: { values: selectedProgramIds.filter((_) => _ !== id) } });
  };

  const handleDismissAllPrograms = () => {
    onPrimaryFilterChange({ programIdSelection: { values: [] } });
  };

  return (
    <Section>
      <Typography2 component="h3" variant="subtitleMedium">
        {_t('Primary filter: #{filter}', {
          filter: getPrimaryFilterLabel(primaryFilter.type),
        })}
      </Typography2>
      <Spacer size={16} />
      <SubSection>
        {programName ? (
          <Typography variant="h2" css={{ margin: 16 }} data-testid="preselected-program">
            {_t('Selected program: #{programName}', { programName })}
          </Typography>
        ) : (
          <Accordion variant="silent">
            <AccordionHeader
              label={_t('Programs: #{programs}', {
                programs: selectedProgramIds.length > 0 ? _t('Selected learning programs') : _t('Any learning program'),
              })}
            />
            <AccordionPanel>
              <Spacer size={16} />
              {selectedProgramIds.length > 0 ? (
                <Grid>
                  <FilterPills
                    label={_t('Selected programs')}
                    filterOptions={convertEnrichedProgramsToFilterOptions(enrichedData, selectedProgramIds)}
                    numFiltersDisplayed={MAX_PROGRAMS_DISPLAYED}
                    onDismiss={(id: string) => handleDismissProgram(normalizeProgramId({ id }))}
                    onMore={openProgramSelectionDrawer('selected')}
                  />
                  <Spacer size={16} />
                  <div css={{ display: 'flex', gap: theme.spacing(16) }}>
                    <Button
                      css={css`
                        ${theme.breakpoints.down('xs')} {
                          width: 100%;
                          max-width: none;
                        }
                      `}
                      variant="secondary"
                      size="small"
                      onClick={openProgramSelectionDrawer('search')}
                      data-testid="primary-filter-section-select_filled"
                    >
                      {_t('Select Programs')}
                    </Button>
                    <Button
                      css={css`
                        ${theme.breakpoints.down('xs')} {
                          width: 100%;
                          max-width: none;
                        }
                      `}
                      variant="secondary"
                      size="small"
                      onClick={handleDismissAllPrograms}
                      data-testid="primary-filter-section-clear-all"
                    >
                      {_t('Clear all programs')}
                    </Button>
                  </div>
                </Grid>
              ) : (
                <Button
                  css={css`
                    ${theme.breakpoints.down('xs')} {
                      width: 100%;
                      max-width: none;
                    }
                  `}
                  variant="secondary"
                  size="small"
                  onClick={openProgramSelectionDrawer('search')}
                  data-testid="primary-filter-section-select_empty"
                >
                  {_t('Select Programs')}
                </Button>
              )}
            </AccordionPanel>
          </Accordion>
        )}
      </SubSection>
      {primaryFilter.type === 'MEMBER' && (
        <React.Fragment>
          <Spacer size={16} />
          <SubSection>
            <ProgramJoinDate
              errors={errors}
              dateClause={primaryFilter.programJoinDate}
              singleProgram={Boolean(programName) || selectedProgramIds.length === 1}
              onChange={(date) => {
                onPrimaryFilterChange({ programJoinDate: date });
                onClearError('programJoinDateFromAfterTo');
              }}
            />
          </SubSection>
        </React.Fragment>
      )}
      <Spacer size={16} />
      <Divider />
      <Spacer size={16} />
      <Button
        css={css`
          ${theme.breakpoints.down('xs')} {
            width: 100%;
            max-width: none;
          }
        `}
        variant="ghost"
        size="small"
        onClick={onChangePrimaryFilter}
        data-testid="change-primary-filter"
      >
        {_t('Change my primary filter')}
      </Button>
      {isProgramSelectionDrawerOpen && (
        <ProgramSelectionDrawer
          selectedPrograms={convertEnrichedProgramsToFilterOptions(enrichedData, selectedProgramIds)}
          isOpen={isProgramSelectionDrawerOpen}
          thirdPartyOrganization={thirdPartyOrganization}
          onApply={(programs) => {
            onPrimaryFilterChange({ programIdSelection: { values: convertFilterOptionsToIds(programs) } });
            onEnrichedProgramsChange(convertFilterOptionsToEnrichedPrograms(programs));
          }}
          onClose={() => setIsProgramSelectionDrawerOpen(false)}
          defaultTab={programSelectionDrawerDefaultTab}
        />
      )}
    </Section>
  );
};

export default PrimaryFilterSection;
